'use client';

import React from 'react';
import classNames from 'classnames';
import type { ArticleBlock } from '@benzinga/article-manager';
import {
  AD_RESERVED_CAPPED_CLASS,
  AD_RESERVED_CLASS,
  AD_RESERVED_SIZED_CLASS,
  IN_CONTENT_SLOT_ID,
  inContentSlotReservedMinStyle,
  isDisplayAdBlockName,
  shouldReserveInContentSlot,
  shouldSuppressInContentInner,
} from '@benzinga/ads-utils';
import { useBenzingaEdge } from '@benzinga/edge';
import { ErrorBoundary } from '@benzinga/core-ui';
import { ArticleBlock as ArticleBlockView } from './ArticleBlock';

type InContentSlotBlock = {
  attrs?: {
    data?: {
      blocks?: ArticleBlock[];
      minHeightPx?: number;
    };
  };
};

export const InContentSlot: React.FC<{ block: InContentSlotBlock }> = ({ block }) => {
  const { adLightEnabled, disabled } = useBenzingaEdge();
  const innerBlocks = block.attrs?.data?.blocks ?? [];
  const minHeightPx = block.attrs?.data?.minHeightPx ?? 350;
  const suppressAds = adLightEnabled || disabled.displayAd;
  const adsOnly = innerBlocks.length > 0 && innerBlocks.every(inner => isDisplayAdBlockName(inner.blockName));
  const reserveSlot = shouldReserveInContentSlot(innerBlocks.map(inner => inner?.blockName));

  return (
    <div
      className={classNames(
        reserveSlot && AD_RESERVED_CLASS,
        reserveSlot && AD_RESERVED_SIZED_CLASS,
        adsOnly && AD_RESERVED_CAPPED_CLASS,
        'w-full',
      )}
      id={IN_CONTENT_SLOT_ID}
      style={reserveSlot ? inContentSlotReservedMinStyle(minHeightPx) : undefined}
    >
      {innerBlocks.map((innerBlock, index) => {
        if (!innerBlock) return null;
        if (shouldSuppressInContentInner(innerBlock.blockName, suppressAds)) return null;
        return (
          <ErrorBoundary
            key={`in-content-slot-${innerBlock.blockName}-${index}`}
            name={`in-content-slot-${innerBlock.blockName}-${index}`}
          >
            <ArticleBlockView block={innerBlock} />
          </ErrorBoundary>
        );
      })}
    </div>
  );
};
